import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../Includes/Sidebar';
import Footer from '../Includes/Footer';
import { Link, useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import GeneralService from '../../services/general.service';

export default function PatientReceiptList() {
    const navigate = useNavigate();
    const [patientTokens, setPatientTokens] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);
    const [doctorFilter, setDoctorFilter] = useState('all');
    const [printedFilter, setPrintedFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadingStates, setLoadingStates] = useState({});
    const [showSummary, setShowSummary] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Today's date

    // Helper function to safely access localStorage
    const safeLocalStorage = {
        getItem: (key) => {
            try {
                return localStorage.getItem(key);
            } catch (error) {
                console.warn('localStorage getItem failed:', error);
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                localStorage.setItem(key, value);
            } catch (error) {
                console.warn('localStorage setItem failed:', error);
            }
        }
    };

    // API Call to Fetch Patient Tokens Data
    const fetchPatientTokens = async () => {
        try {
            setIsLoading(true);
            const response = await GeneralService.getPatientTokens();
            console.log("API Response:", response.data);

            if (Array.isArray(response.data)) {
                const tokensWithIds = response.data.map((token, index) => {
                    if (!token.id) {
                        console.warn(`Token at index ${index} has no ID, using index as fallback:`, token);
                        return { ...token, id: token.token_id || index + 1 };
                    }
                    return token;
                });
                
                // Sort by latest first (based on record_date or created_at)
                const sortedTokens = tokensWithIds.sort((a, b) => {
                    const dateA = new Date(a.record_date || a.created_at || 0);
                    const dateB = new Date(b.record_date || b.created_at || 0);
                    return dateB - dateA; // Latest first
                });
                setPatientTokens(sortedTokens);
            } else if (response.data && response.data.data) {
                if (Array.isArray(response.data.data)) {
                    const sortedTokens = response.data.data.sort((a, b) => {
                        const dateA = new Date(a.record_date || a.created_at || 0);
                        const dateB = new Date(b.record_date || b.created_at || 0);
                        return dateB - dateA;
                    });
                    setPatientTokens(sortedTokens);
                } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    const sortedTokens = response.data.data.data.sort((a, b) => {
                        const dateA = new Date(a.record_date || a.created_at || 0);
                        const dateB = new Date(b.record_date || b.created_at || 0);
                        return dateB - dateA;
                    });
                    setPatientTokens(sortedTokens);
                } else {
                    console.error("Unexpected response format:", response.data);
                    setPatientTokens([]);
                }
            } else {
                console.error("Unexpected response format:", response.data);
                setPatientTokens([]);
            }
        } catch (error) {
            console.error("Error fetching patient tokens:", error);
            console.error("Error details:", error.response?.data);
            setPatientTokens([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle delete function
    const handleDelete = async (tokenId) => {
        if (!tokenId) {
            alert("Token ID is missing. Cannot delete.");
            return;
        }

        try {
            await GeneralService.deletePatientToken(tokenId);
            fetchPatientTokens();
        } catch (error) {
            console.error("Error deleting patient token:", error);
            alert("Failed to delete patient token. Please try again.");
        }
    };

    // Handle mark as printed with loading state
    const handleMarkAsPrinted = async (tokenId) => {
        if (!tokenId) {
            alert("Token ID is missing. Cannot mark as printed.");
            return;
        }

        setLoadingStates(prev => ({ ...prev, [tokenId]: true }));

        try {
            await GeneralService.markTokenAsPrinted(tokenId);
            
            setPatientTokens(prev => 
                prev.map(token => 
                    token.id === tokenId 
                        ? { ...token, is_printed: true }
                        : token
                )
            );
            
        } catch (error) {
            console.error("Error marking token as printed:", error);
            // alert("Failed to mark token as printed. Please try again.");
        } finally {
            setLoadingStates(prev => {
                const newState = { ...prev };
                delete newState[tokenId];
                return newState;
            });
        }
    };

    // Print function with proper data handling
 const handlePrintReceiptWithData = (token) => {
    const receiptId = `RCP${Date.now()}`;
    
    // Fix: Add null/undefined checks for patient_name
    const patientName = token.patient_name || 'Unknown Patient';
    const clientIdPrefix = patientName.length >= 3 ? patientName.slice(0, 3).toUpperCase() : 'PAT';
    
    const printData = {
        clientId: clientIdPrefix + Math.floor(Math.random() * 1000),
        order: receiptId,
        customerName: patientName,
        phone: token.phone || '0300-0000000',
        address: token.address || 'Patient Address',
        date: token.appointment_date || new Date().toISOString(),
        tokenNumber: token.token_number || 'N/A',
        doctorName: token.doctor_name || 'Unknown Doctor',
        deliveryCharges: 0,
        items: (() => {
            try {
                const services = typeof token.services === 'string' ? JSON.parse(token.services) : token.services;
                if (Array.isArray(services) && services.length > 0) {
                    return services.map(service => ({
                        prod_name: service.serviceName || service.name || 'Service',
                        quantity: '1',
                        prod_price: service.price?.toString() || '0'
                    }));
                }
                return [{
                    prod_name: 'Consultation Fee',
                    quantity: '1',
                    prod_price: token.total_amount?.toString() || '0'
                }];
            } catch (error) {
                console.error('Error parsing services:', error);
                return [{
                    prod_name: 'Consultation Fee',
                    quantity: '1',
                    prod_price: token.total_amount?.toString() || '0'
                }];
            }
        })()
    };

    try {
        // Additional validation before printing
        if (!token.id) {
            alert('Token ID is missing. Cannot proceed with printing.');
            return;
        }

        safeLocalStorage.setItem('print_data', JSON.stringify(printData));
        window.receiptData = printData;
        
        const printWindow = window.open('/printable3.html', '_blank', 'width=800,height=600');
        
        if (printWindow) {
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }, 1000);
            };
        } else {
            alert('Pop-up blocked. Please allow pop-ups for printing.');
            return;
        }

        // Mark as printed after successful print setup
        handleMarkAsPrinted(token.id);
    } catch (error) {
        console.error('Error printing receipt:', error);
        alert('Error occurred while printing receipt: ' + error.message);
    }
};

    useEffect(() => {
        fetchPatientTokens();
        // Set today's date as default filter on component mount
        setDateFilter(getTodayDate());
    }, []);

    const handleSearch = useCallback(
        debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 300),
        []
    );

    useEffect(() => {
        return () => handleSearch.cancel();
    }, [handleSearch]);

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Get formatted today's date for display
    const getTodayFormatted = () => {
        const today = new Date();
        const options = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return today.toLocaleDateString('en-US', options);
    };

    // Get current filter date formatted for display
    const getCurrentFilterDateFormatted = () => {
        if (!dateFilter) return 'All Dates';
        const filterDate = new Date(dateFilter);
        const options = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return filterDate.toLocaleDateString('en-US', options);
    };

    // Check if current filter is today
    const isShowingToday = () => {
        return dateFilter === getTodayDate();
    };

    const extractDateFromISO = (isoString) => {
        if (!isoString) return null;
        // Handle both ISO format (2025-06-28T00:00:00.000000Z) and regular format (2025-06-28)
        return isoString.split('T')[0]; // This will give us YYYY-MM-DD format
    };

    // Filter patient tokens based on search query and filters
    const filteredTokens = patientTokens.filter(token => {
        // Fixed date filtering logic
        const tokenDate = extractDateFromISO(token.record_date);
        const matchesDate = dateFilter === '' || tokenDate === dateFilter;
        
        return (token.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                token.doctor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                token.token_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                token.phone?.includes(searchQuery)) &&
               (doctorFilter === 'all' || token.doctor_name?.toLowerCase().includes(doctorFilter.toLowerCase())) &&
               (printedFilter === 'all' || 
                (printedFilter === 'printed' && token.is_printed) ||
                (printedFilter === 'not_printed' && !token.is_printed)) &&
               matchesDate;
    });

    // Sort filtered tokens by token number for ANY date (not just today)
    const sortedFilteredTokens = filteredTokens.sort((a, b) => {
        // First, group by date if showing multiple dates
        if (!dateFilter) {
            // If no date filter, sort by date first (latest first), then by token number
            const dateA = new Date(a.record_date || a.created_at || 0);
            const dateB = new Date(b.record_date || b.created_at || 0);
            
            if (dateA.getTime() !== dateB.getTime()) {
                return dateB - dateA; // Latest date first
            }
        }
        
        // Sort by token number within the same date
        const tokenA = a.token_number || '';
        const tokenB = b.token_number || '';
        
        // Extract prefix and number
        const prefixA = tokenA.replace(/[0-9]/g, '');
        const prefixB = tokenB.replace(/[0-9]/g, '');
        const numberA = parseInt(tokenA.replace(/[^0-9]/g, '')) || 0;
        const numberB = parseInt(tokenB.replace(/[^0-9]/g, '')) || 0;
        
        // First sort by prefix, then by number
        if (prefixA !== prefixB) {
            return prefixA.localeCompare(prefixB);
        }
        return numberA - numberB; // A001, A002, A003... order
    });

    // Get today's tokens for quick access
    const todayTokens = patientTokens.filter(token => {
        const tokenDate = extractDateFromISO(token.record_date);
        return tokenDate === getTodayDate();
    });

    // Calculate summary for selected date
    const getDateSummary = (date) => {
        const dateTokens = patientTokens.filter(token => {
            const tokenDate = extractDateFromISO(token.record_date);
            return tokenDate === date;
        });

        const totalPatients = dateTokens.length;
        const printedTokens = dateTokens.filter(token => token.is_printed).length;
        const notPrintedTokens = totalPatients - printedTokens;
        const totalAmount = dateTokens.reduce((sum, token) => sum + (parseFloat(token.total_amount) || 0), 0);
        
        // Get unique doctors for the date
        const uniqueDoctorsForDate = [...new Set(dateTokens.map(token => token.doctor_name).filter(Boolean))];

        return {
            totalPatients,
            printedTokens,
            notPrintedTokens,
            totalAmount,
            doctorsCount: uniqueDoctorsForDate.length,
            doctors: uniqueDoctorsForDate
        };
    };

    const totalRecords = sortedFilteredTokens.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const displayedTokens = sortedFilteredTokens.slice(startIndex, startIndex + recordsPerPage);

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format services array
    const formatServices = (services) => {
        if (!services) return 'N/A';
        
        try {
            const serviceArray = typeof services === 'string' ? JSON.parse(services) : services;
            if (Array.isArray(serviceArray)) {
                return serviceArray.map(service => service.serviceName || service.name).join(', ');
            }
            return 'N/A';
        } catch (error) {
            console.error('Error parsing services:', error);
            return 'N/A';
        }
    };

    // Get unique doctors for filter
    const uniqueDoctors = [...new Set(patientTokens.map(token => token.doctor_name).filter(Boolean))];

    // Get printed status badge class
    const getPrintedBadgeClass = (isPrinted) => {
        return isPrinted ? 'bg-success' : 'bg-warning';
    };

    // Get printed status text
    const getPrintedStatusText = (isPrinted) => {
        return isPrinted ? 'Printed' : 'Not Printed';
    };

    // Handle show today's data
    const showTodayData = () => {
        setDateFilter(getTodayDate());
        setCurrentPage(1);
    };

    // Handle clear filters
    const clearFilters = () => {
        setDateFilter('');
        setSearchQuery('');
        setDoctorFilter('all');
        setPrintedFilter('all');
        setCurrentPage(1);
    };

    return (
        <div className="content">
            <Sidebar />
            <div className="container-fluid pt-4 px-4">
                <div className="bg-light text-center rounded p-4">
                    {/* Enhanced Header with Current Date Info */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <h5 className="mb-1">
                                Patient Receipts <span className="number-records">({totalRecords})</span>
                            </h5>
                            <div className={`small ${isShowingToday() ? 'text-primary fw-bold' : 'text-muted'}`}>
                                {isShowingToday() ? (
                                    <>
                                        <i className="fas fa-calendar-check me-1"></i>
                                        <strong>Today: {getTodayFormatted()}</strong>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-calendar me-1"></i>
                                        Showing: {getCurrentFilterDateFormatted()}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <button 
                                className={`btn btn-sm ${isShowingToday() ? 'btn-success' : 'btn-primary'}`}
                                onClick={showTodayData}
                                title="Show Today's Records"
                            >
                                <i className="fas fa-calendar-day me-1"></i>
                                {isShowingToday() ? 'Today (Current)' : `Today (${todayTokens.length})`}
                            </button>
                            <button 
                                className="btn btn-info btn-sm"
                                onClick={() => setShowSummary(!showSummary)}
                                title="Show/Hide Summary"
                            >
                                <i className="fas fa-chart-bar me-1"></i>
                                Summary
                            </button>
                            <button 
                                className="btn btn-secondary btn-sm"
                                onClick={clearFilters}
                                title="Clear All Filters"
                            >
                                <i className="fas fa-times me-1"></i>
                                Clear Filters
                            </button>
                        </div>
                    </div>

                    {/* Today's Highlight Banner */}
                    {isShowingToday() && (
                        <div className="alert alert-success mb-3">
                            <i className="fas fa-star me-2"></i>
                            <strong>Today's Tokens:</strong> Showing {totalRecords} tokens for today, sorted by token number (A001, A002, etc.)
                        </div>
                    )}

                    {/* Summary Section */}
                    {showSummary && (
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header bg-info text-white">
                                        <h6 className="mb-0">
                                            <i className="fas fa-calendar me-2"></i>
                                            Date Summary for: 
                                            <input 
                                                type="date" 
                                                className="form-control d-inline-block ms-2" 
                                                style={{width: 'auto'}}
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                            />
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        {(() => {
                                            const summary = getDateSummary(selectedDate);
                                            return (
                                                <div className="row">
                                                    <div className="col-md-2">
                                                        <div className="text-center">
                                                            <h4 className="text-primary">{summary.totalPatients}</h4>
                                                            <small>Total Patients</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center">
                                                            <h4 className="text-success">{summary.printedTokens}</h4>
                                                            <small>Printed</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center">
                                                            <h4 className="text-warning">{summary.notPrintedTokens}</h4>
                                                            <small>Not Printed</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center">
                                                            <h4 className="text-info">Rs. {summary.totalAmount.toFixed(2)}</h4>
                                                            <small>Total Amount</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center">
                                                            <h4 className="text-secondary">{summary.doctorsCount}</h4>
                                                            <small>Doctors</small>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <button 
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => {
                                                                setDateFilter(selectedDate);
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            <i className="fas fa-filter me-1"></i>
                                                            Filter This Date
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filters Section */}
                    <div className="row mb-3">
                        <div className="col-md-12">
                            <div className="d-flex gap-2 flex-wrap">
                                <select 
                                    className="form-control" 
                                    value={printedFilter}
                                    onChange={(e) => { 
                                        setPrintedFilter(e.target.value); 
                                        setCurrentPage(1); 
                                    }}
                                >
                                    <option value="all">All Status</option>
                                    <option value="printed">Printed</option>
                                    <option value="not_printed">Not Printed</option>
                                </select>
                                <select 
                                    className="form-control" 
                                    value={doctorFilter}
                                    onChange={(e) => { 
                                        setDoctorFilter(e.target.value); 
                                        setCurrentPage(1); 
                                    }}
                                >
                                    <option value="all">All Doctors</option>
                                    {uniqueDoctors.map(doctor => (
                                        <option key={doctor} value={doctor}>{doctor}</option>
                                    ))}
                                </select>
                                <input 
                                    type="date" 
                                    className="form-control"
                                    value={dateFilter}
                                    onChange={(e) => { 
                                        setDateFilter(e.target.value); 
                                        setCurrentPage(1); 
                                    }}
                                    placeholder="Filter by date"
                                />
                                <input 
                                    type="text" 
                                    placeholder="Search patient receipts..." 
                                    className="form-control" 
                                    onChange={(e) => handleSearch(e.target.value)} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Current Filter Status */}
                    {(dateFilter || doctorFilter !== 'all' || printedFilter !== 'all' || searchQuery) && (
                        <div className="alert alert-info mb-3">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Active Filters: </strong>
                            {dateFilter && (
                                <span className={`badge me-1 ${isShowingToday() ? 'bg-success' : 'bg-primary'}`}>
                                    Date: {dateFilter} {isShowingToday() && '(Today)'}
                                </span>
                            )}
                            {doctorFilter !== 'all' && <span className="badge bg-success me-1">Doctor: {doctorFilter}</span>}
                            {printedFilter !== 'all' && <span className="badge bg-warning me-1">Status: {printedFilter}</span>}
                            {searchQuery && <span className="badge bg-secondary me-1">Search: {searchQuery}</span>}
                        </div>
                    )}

                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="bg-primary">
                                <tr className="text-white">
                                    <th>No</th>
                                    <th>Token Number</th>
                                    <th>Patient Name</th>
                                    <th>Phone</th>
                                    <th>Doctor Name</th>
                                    <th>Address</th>
                                    <th>Appointment Date</th>
                                    <th>Appointment Time</th>
                                    <th>Total Amount</th>
                                    <th>Services</th>
                                    <th>Record Date</th>
                                    <th>Printed Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="13" className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayedTokens.length > 0 ? (
                                    displayedTokens.map((token, index) => (
                                        <tr key={token.id || index} className={isShowingToday() ? 'table-row-today' : ''}>
                                            <td>{startIndex + index + 1}</td>
                                            <td>
                                                <span className={`fw-bold ${isShowingToday() ? 'text-success' : 'text-primary'}`}>
                                                    {token.token_number || 'N/A'}
                                                </span>
                                            </td>
                                            <td>{token.patient_name || 'N/A'}</td>
                                            <td>
                                                <a href={`tel:${token.phone}`} className="text-decoration-none">
                                                    {token.phone || 'N/A'}
                                                </a>
                                            </td>
                                            <td>{token.doctor_name || 'N/A'}</td>
                                            <td>
                                                <span title={token.address || 'N/A'}>
                                                    {token.address ? 
                                                        (token.address.length > 20 ? 
                                                            token.address.substring(0, 20) + '...' : 
                                                            token.address
                                                        ) : 'N/A'
                                                    }
                                                </span>
                                            </td>
                                            <td>{token.appointment_date || 'N/A'}</td>
                                            <td>{token.appointment_time || 'N/A'}</td>
                                            <td>
                                                <span className="fw-bold text-success">
                                                    Rs. {token.total_amount || '0'}
                                                </span>
                                            </td>
                                            <td>
                                                <span title={formatServices(token.services)}>
                                                    {formatServices(token.services).length > 30 ? 
                                                        formatServices(token.services).substring(0, 30) + '...' : 
                                                        formatServices(token.services)
                                                    }
                                                </span>
                                            </td>
                                            <td>{token.record_date || 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${getPrintedBadgeClass(token.is_printed)}`}>
                                                    {getPrintedStatusText(token.is_printed)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {loadingStates[token.id] ? (
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {!token.is_printed && (
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    title="Print and Mark as Printed"
                                                                    onClick={() => handlePrintReceiptWithData(token)}
                                                                    disabled={loadingStates[token.id]}
                                                                >
                                                                    <i className="fas fa-print me-1"></i>
                                                                    Print
                                                                </button>
                                                            )}
                                                            {/* <button
                                                                className="btn btn-sm btn-info"
                                                                title="View Details"
                                                                onClick={() => navigate(`/patient-receipt/${token.id}`)}
                                                            >
                                                                <i className="fas fa-eye me-1"></i>
                                                                View
                                                            </button> */}
                                                            {/* <button
                                                                className="btn btn-sm btn-warning"
                                                                title="Edit"
                                                                onClick={() => navigate(`/patient-receipt/edit/${token.id}`)}
                                                            >
                                                                <i className="fas fa-edit me-1"></i>
                                                                Edit
                                                            </button> */}
                                                            {/* <button 
                                                                className="btn btn-sm btn-danger"
                                                                title="Delete"
                                                                onClick={() => {
                                                                    if (window.confirm('Are you sure you want to delete this patient receipt?')) {
                                                                        handleDelete(token.id);
                                                                    }
                                                                }}
                                                            >
                                                                <i className="fas fa-trash me-1"></i>
                                                                Delete
                                                            </button> */}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="13" className="text-center">
                                            {isShowingToday() ? 
                                                "No patient receipts found for today" : 
                                                "No patient receipts found"
                                            }
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && totalPages > 1 && (
                        <div className="pagination-container mt-3">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage - 1)} 
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages).keys()].map((num) => (
                                    <li 
                                        key={num + 1} 
                                        className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}
                                    >
                                        <button 
                                            className="page-link" 
                                            onClick={() => setCurrentPage(num + 1)}
                                        >
                                            {num + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage + 1)} 
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}